import React, { Fragment } from "react";
import { ReactTabulator } from "react-tabulator";
import { simpleSort } from "../../utils/simpleSort";
import i18n from "../../i18n";
import LanguageAwarePureComponent from "./LanguageAwarePureComponent";

/**
 * Maps i18n language codes to proper locale codes for ReactTabulator
 */
const getLocaleForTable = () => {
  const currentLang = i18n.language || 'en';
  // Map 'no' to 'nb-NO' for proper Norwegian character sorting (æ, ø, å)
  return currentLang === 'no' ? 'nb-NO' : currentLang;
};

/**
 * Custom sorter function for Norwegian-aware text sorting
 * Centralized sorting logic with proper error handling
 */
const customTextSorter = (a, b, aRow, bRow, column, dir, sorterParams) => {
  try {
    // Use our centralized simpleSort function for proper Norwegian sorting
    const result = simpleSort(a, b);
    return dir === "desc" ? -result : result;
  } catch (error) {
    console.warn('Sorting failed in customTextSorter:', error);
    // Fallback to basic string comparison
    const aStr = String(a || '');
    const bStr = String(b || '');
    const result = aStr.localeCompare(bStr);
    return dir === "desc" ? -result : result;
  }
};

class CommonTable extends LanguageAwarePureComponent {
    ref = React.createRef();
    _isMounted = false;

    state = {
        t: this.props.t,
        columns: this.props.columns.map(col => ({
            ...col,
            sorter: col.sorter || customTextSorter // Ensure all columns use Norwegian-aware sorter
        })),
        data: this.props.data,
        options: {
            pagination: "local",
            paginationSize: 10,
            initialSort: this.props.initialSort || [
                {column: "name", dir: "asc"} // Default sort by name column
            ],
            // Use ReactTabulator's built-in locale support with proper locale mapping
            locale: getLocaleForTable(),
            // Add custom Norwegian-aware sorting for text columns
            columnDefaults: {
                sorter: customTextSorter, // Use our custom sorter for all columns by default
            },
            langs: {
                all: {
                    pagination: {
                        first: this.props.t("common.table.pagination.first"),
                        first_title: this.props.t("common.table.pagination.first_title"),
                        last: this.props.t("common.table.pagination.last"),
                        last_title: this.props.t("common.table.pagination.last_title"),
                        prev: this.props.t("common.table.pagination.prev"),
                        prev_title: this.props.t("common.table.pagination.prev_title"),
                        next: this.props.t("common.table.pagination.next"),
                        next_title: this.props.t("common.table.pagination.next_title")
                    }
                }
            },
            rowFormatter: function (row) {
                var data = row.getData();
                if (data.isNewWorkflow) {
                    row.getElement().className = row.getElement().className + " new_workflow";
                }
            }
        }
    };

    componentDidMount() {
        // Call parent componentDidMount first
        super.componentDidMount();
        this._isMounted = true;
    }

    componentWillUnmount() {
        // Call parent componentWillUnmount first
        super.componentWillUnmount();
        this._isMounted = false;
    }

    /**
     * Safely execute tabulator methods with proper checks
     * @param {Function} callback - Function to execute if table is safe to use
     */
    safeExecuteOnTable = (callback) => {
        if (!this._isMounted || !this.ref?.current) {
            return;
        }
        
        try {
            // Additional check to ensure the table element is still in DOM
            const tableElement = this.ref.current.element;
            if (tableElement && tableElement.offsetParent !== null) {
                callback();
            }
        } catch (error) {
            console.warn('Error executing table operation:', error);
        }
    };

    onLanguageChanged = (newLanguage) => {
        // Force update of columns with new sorter (since locale context changed)
        const columnsWithSorter = this.props.columns.map(col => ({
            ...col,
            sorter: col.sorter || customTextSorter // Reapply custom sorter with new locale context
        }));
        
        this.setState({ 
            columns: columnsWithSorter, // Update columns to trigger ReactTabulator refresh
            options: {
                ...this.state.options,
                locale: getLocaleForTable(),
                columnDefaults: {
                    sorter: customTextSorter, // Ensure custom sorter is maintained
                }
            }
        }, () => {
            this.safeExecuteOnTable(() => {
                // Set the new locale on the table
                this.ref.current.setLocale(getLocaleForTable());
                
                // Force a complete redraw to ensure sorting is updated
                setTimeout(() => {
                    this.safeExecuteOnTable(() => {
                        this.ref.current.redraw(true);
                        // If there's existing sort, reapply it to use new locale
                        const currentSort = this.ref.current.getSorters();
                        if (currentSort && currentSort.length > 0) {
                            // Clear and reapply sort to use new locale-aware sorter
                            this.ref.current.clearSort();
                            setTimeout(() => {
                                this.safeExecuteOnTable(() => {
                                    currentSort.forEach(sort => {
                                        this.ref.current.setSort([{column: sort.field, dir: sort.dir}]);
                                    });
                                });
                            }, 10);
                        }
                    });
                }, 50);
            });
        });
    };

    componentDidUpdate(prevProps, prevState) {
        // Update props when they change OR when language changes
        if (prevProps.columns !== this.props.columns || 
            prevProps.data !== this.props.data || 
            prevProps.t !== this.props.t ||
            prevProps.initialSort !== this.props.initialSort ||
            prevState.currentLanguage !== this.state.currentLanguage) {
            
            // Ensure all columns use our Norwegian-aware sorter unless they explicitly specify another
            const columnsWithSorter = this.props.columns.map(col => ({
                ...col,
                sorter: col.sorter || customTextSorter // Use custom sorter if no specific sorter is defined
            }));
            
            this.setState({ 
                columns: columnsWithSorter, 
                data: this.props.data, 
                t: this.props.t,
                options: {
                    ...this.state.options,
                    locale: getLocaleForTable(),
                    columnDefaults: {
                        sorter: customTextSorter, // Ensure custom sorter is maintained
                    },
                    initialSort: this.props.initialSort || this.state.options.initialSort
                }
            }, () => {
                // Force ReactTabulator to update by refreshing its configuration
                // Use a timeout to ensure the state has been applied
                setTimeout(() => {
                    // Force a redraw of the table to ensure new column configs are applied
                    this.safeExecuteOnTable(() => {
                        this.ref.current.redraw(true);
                    });
                }, 50);
            });
        }
    }

    setRef(ref) {
        if (ref) {
            this.ref = ref;
            let _this = this;
            setTimeout(() => {
                _this.safeExecuteOnTable(() => {
                    ref.current.setLocale(getLocaleForTable());
                });
            }, 100);

            // Set up row click handler safely
            setTimeout(() => {
                _this.safeExecuteOnTable(() => {
                    _this.ref.current.on("rowClick", function (e, row) {
                        _this.safeExecuteOnTable(() => {
                            _this.ref.current.deselectRow();
                            _this.ref.current.selectRow(row);
                        });

                        _this.props.rowClick(row.getData());
                    });
                });
            }, 10);
        }
    }

    render() {
        // Ensure all columns have the Norwegian-aware sorter applied at render time
        const columnsWithSorter = this.state.columns.map(col => ({
            ...col,
            sorter: col.sorter || customTextSorter // Apply custom sorter if none specified
        }));
        
        const { data, options } = this.state;
        return (
            <Fragment>
                <div className="row">
                    <div className="col-xs-12">
                        <div className="box">
                            <div className="box-body">
                                {data.length > 0 && (
                                    <ReactTabulator
                                        onRef={ref => this.setRef(ref)}
                                        columns={columnsWithSorter}
                                        data={data}
                                        options={options}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default CommonTable;